import { useCallback, useState } from 'react';
import { parseJSON, normalizeData, ParseError } from '@/lib/parsers';
import { useApp } from '@/contexts/AppContext';

export interface UploadError {
  message: string;
  type: 'validation' | 'parse' | 'file';
}

export function useFileUpload() {
  const { setAppState, setFileInfo } = useApp();
  const [error, setError] = useState<UploadError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setIsLoading(true);

      try {
        // Validate file type
        if (!file.name.endsWith('.json')) {
          throw new Error('Please upload a JSON file');
        }

        // Read file
        const text = await file.text();
        
        // Parse and normalize
        const rawData = parseJSON(text);
        const normalizedState = normalizeData(rawData);

        // Update app state
        setAppState(normalizedState);
        setFileInfo(file.name, file.size);

        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        const errorType = err instanceof ParseError ? 'parse' : 'validation';
        
        setError({
          message: errorMessage,
          type: errorType,
        });
        
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setAppState, setFileInfo]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    handleFile,
    error,
    isLoading,
    clearError,
    reset,
  };
}
